import { Injectable, NgZone, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, fromEvent, merge, throttleTime } from 'rxjs';
import { IControlActividad } from 'modelos/src';
import { HelperService } from './helper.service';
import { EstadisticasUsoService } from './http/estadisticas-uso.service';
import { LoginService } from '../../modulos/login/login.service';
import {
  ExcesoActividadDialogComponent,
  ExcesoActividadData,
  PosicionDrag,
} from '../exceso-actividad-dialog/exceso-actividad-dialog.component';

/**
 * Control de periodo de actividad de operadores. Análogo (invertido) al
 * InactividadUsuarioService: en vez de medir ausencia de interacción, acumula
 * el tiempo de USO continuo. Al superar el límite configurado
 * (cliente.config.controlActividad.limTiempoActividad) muestra un banner
 * informativo NO bloqueante que sugiere tomar un descanso y registra el exceso.
 *
 * El contador se acumula por muestreo: cada `INTERVALO_MUESTREO` se suma el
 * intervalo al total sólo si hubo interacción (mouse/teclado/touch/wheel) en la
 * ventana y la pestaña está visible; así el tiempo con el operador ausente no
 * cuenta como actividad. El contador SÓLO se resetea cuando el operador acepta
 * el banner y termina el descanso fijo (`limTiempoDescanso`).
 *
 * Solo aplica a permisos con rol Operador (ver HelperService.esRolOperador).
 */
@Injectable({
  providedIn: 'root',
})
export class ActividadUsuarioService {
  private matDialog = inject(MatDialog);
  private helper = inject(HelperService);
  private ngZone = inject(NgZone);
  private estadisticasUso = inject(EstadisticasUsoService);

  /** Cada cuánto se muestrea la actividad (ms). */
  private readonly INTERVALO_MUESTREO = 30_000;

  private activo = false;
  private eventosSuscriptos = false;
  /** Actividad detectada en la ventana de muestreo actual. */
  private huboInteraccion = false;
  /** Ms de uso acumulados en el ciclo actual. */
  private msActivos = 0;
  /** Comienzo del ciclo de uso actual (se envía como `inicio` del registro). */
  private cicloInicio = Date.now();
  /** true entre que se muestra el banner y termina el descanso (pausa el conteo). */
  private avisado = false;
  /** true mientras corre el descanso (tras aceptar el banner y hasta que cierra).
   *  Lo consume InactividadUsuarioService para pausar su control: durante el
   *  descanso el operador puede alejarse sin que se dispare la inactividad. */
  public enDescanso$ = new BehaviorSubject(false);
  private sampler?: NodeJS.Timeout;
  private dialogRef?: MatDialogRef<ExcesoActividadDialogComponent, boolean>;

  /** Epoch (ms) en que termina el descanso en curso; undefined si no hay. Se
   *  persiste para reanudar el descanso tras un refresco. */
  private descansoHasta?: number;
  /** Si el banner de descanso estaba minimizado (chip). Se persiste. */
  private minimizado = false;
  /** Posición del drag por estado; se persiste para reanudar dónde se dejó. */
  private posMinimizado?: PosicionDrag;
  private posExpandido?: PosicionDrag;
  /** El estado se restaura una sola vez por carga de página. */
  private restaurado = false;
  private static readonly STORAGE_PREFIX = 'gpe.controlActividad.';

  /** Idempotente. Llamar al iniciar sesión y en cada cambio de permiso:
   *  re-evalúa el rol y arma o desarma el control según corresponda. */
  public iniciarServicio(): void {
    this.activo =
      !!LoginService.getAccessToken() &&
      this.helper.esRolOperador() &&
      !!this.getConfig();
    if (!this.activo) {
      this.detener();
      return;
    }
    if (!this.eventosSuscriptos) {
      this.eventosSuscriptos = true;
      // Fuera de la zona de Angular: sólo prendemos un flag, no hace falta
      // change detection por cada evento (igual que el control de inactividad).
      this.ngZone.runOutsideAngular(() => {
        merge(
          fromEvent(document, 'mousemove'),
          fromEvent(document, 'touchstart'),
          fromEvent(document, 'keydown'),
          fromEvent(document, 'wheel'),
        )
          .pipe(throttleTime(1000))
          .subscribe(() => (this.huboInteraccion = true));
      });
    }
    if (!this.sampler) {
      this.ngZone.runOutsideAngular(() => {
        this.sampler = setInterval(
          () => this.onMuestreo(),
          this.INTERVALO_MUESTREO,
        );
      });
    }
    // Restaura contador/descanso persistidos (una vez por carga de página).
    if (!this.restaurado) {
      this.restaurado = true;
      this.restaurarEstado();
    }
  }

  /** Config del cliente propio. Devuelve null si no está configurado el límite
   *  de actividad — sin él el control no corre. Se lee en cada uso para tomar
   *  cambios de configuración sin recargar. */
  private getConfig(): Required<IControlActividad> | null {
    const cliente =
      LoginService.getClientePropio() || LoginService.getCliente();
    const config = cliente?.config?.controlActividad;
    if (!config?.limTiempoActividad || !config?.limTiempoDescanso) {
      return null;
    }
    return {
      limTiempoActividad: config.limTiempoActividad,
      limTiempoDescanso: config.limTiempoDescanso,
    };
  }

  private onMuestreo(): void {
    // Mientras el banner/descanso está abierto no se acumula.
    if (!this.activo || this.avisado) return;

    // La sesión pudo expirar (401 → /login) con el control todavía armado.
    if (!LoginService.getAccessToken()) {
      this.detener();
      return;
    }
    // Le pudieron sacar la config al cliente con el control armado.
    const config = this.getConfig();
    if (!config) {
      this.detener();
      return;
    }

    const interaccion = this.huboInteraccion;
    this.huboInteraccion = false;
    // Sólo cuenta como uso si hubo interacción y la pestaña está visible.
    if (!interaccion || document.visibilityState !== 'visible') return;

    this.msActivos += this.INTERVALO_MUESTREO;
    this.persistir();
    if (this.msActivos >= config.limTiempoActividad * 60_000) {
      this.onExceso(config);
    }
  }

  private onExceso(config: Required<IControlActividad>): void {
    if (this.avisado) return;
    this.avisado = true;

    // Registro del exceso (fire-and-forget). `inicio` = comienzo del ciclo.
    this.estadisticasUso
      .registrarPeriodoActividad(new Date(this.cicloInicio).toISOString())
      .catch(() => {});

    // El muestreo corre fuera de la zona → re-entrar para que el banner
    // renderice.
    this.ngZone.run(() => this.abrirBanner(config));
  }

  /** Abre el banner. Sin `segundosRestantes` arranca en fase aviso (flujo
   *  normal); con él reanuda directo en descanso tras un refresco. */
  private abrirBanner(
    config: Required<IControlActividad>,
    segundosRestantes?: number,
  ): void {
    const data: ExcesoActividadData = {
      minutosActividad: config.limTiempoActividad,
      minutosDescanso: config.limTiempoDescanso,
      segundosRestantesInicial: segundosRestantes,
      // Sólo aplica en reanudación (segundosRestantes definido).
      minimizadoInicial: segundosRestantes != null ? this.minimizado : false,
      posicionMinimizadoInicial:
        segundosRestantes != null ? this.posMinimizado : undefined,
      posicionExpandidoInicial:
        segundosRestantes != null ? this.posExpandido : undefined,
    };
    this.dialogRef = this.matDialog.open(ExcesoActividadDialogComponent, {
      width: '420px',
      panelClass: 'custom-dialog',
      // NO bloqueante: sin backdrop el operador puede seguir trabajando e
      // ignorar el banner. Cierra sólo por el botón / fin del descanso.
      hasBackdrop: false,
      disableClose: true,
      autoFocus: false,
      position: { bottom: '24px', right: '24px' },
      data,
    });
    // Al aceptar el banner arranca el descanso → pausa la inactividad y fija el
    // fin del descanso para poder reanudarlo tras un refresco.
    this.dialogRef.componentInstance.descansoIniciado.subscribe(() => {
      this.descansoHasta = Date.now() + config.limTiempoDescanso * 60_000;
      this.persistir();
      this.enDescanso$.next(true);
    });
    // Persistir minimizado/expandido para reanudar igual tras un refresco.
    this.dialogRef.componentInstance.minimizadoChange.subscribe((min) => {
      this.minimizado = min;
      this.persistir();
    });
    // Persistir la posición del drag (por estado) al soltar.
    this.dialogRef.componentInstance.posicionChange.subscribe((p) => {
      if (p.minimizado) {
        this.posMinimizado = { x: p.x, y: p.y };
      } else {
        this.posExpandido = { x: p.x, y: p.y };
      }
      this.persistir();
    });
    this.dialogRef.afterClosed().subscribe((completado) => {
      this.dialogRef = undefined;
      this.descansoHasta = undefined;
      this.enDescanso$.next(false);
      // Sólo se resetea el contador si el descanso se completó (aceptó el
      // banner y esperó). Si se cerró por detener(), no se resetea.
      if (completado) {
        this.reiniciarCiclo();
      }
      this.persistir();
      this.avisado = false;
    });
  }

  private reiniciarCiclo(): void {
    this.msActivos = 0;
    this.cicloInicio = Date.now();
    this.descansoHasta = undefined;
    this.minimizado = false;
    this.posMinimizado = undefined;
    this.posExpandido = undefined;
    this.huboInteraccion = false;
  }

  // --- Persistencia (sobrevive al refresco). Clave por usuario. ---

  private claveStorage(): string | null {
    const id = LoginService.getUsuario()?._id;
    return id ? `${ActividadUsuarioService.STORAGE_PREFIX}${id}` : null;
  }

  private persistir(): void {
    const clave = this.claveStorage();
    if (!clave) return;
    try {
      localStorage.setItem(
        clave,
        JSON.stringify({
          cicloInicio: this.cicloInicio,
          msActivos: this.msActivos,
          descansoHasta: this.descansoHasta,
          minimizado: this.minimizado,
          posMinimizado: this.posMinimizado,
          posExpandido: this.posExpandido,
        }),
      );
    } catch {
      /* storage lleno / bloqueado: el control sigue igual, sólo sin persistir */
    }
  }

  /** Reabre el descanso en curso o restaura el contador de uso tras refrescar. */
  private restaurarEstado(): void {
    const clave = this.claveStorage();
    if (!clave) return;
    const config = this.getConfig();
    if (!config) return;

    let estado: {
      cicloInicio?: number;
      msActivos?: number;
      descansoHasta?: number;
      minimizado?: boolean;
      posMinimizado?: PosicionDrag;
      posExpandido?: PosicionDrag;
    } | null = null;
    try {
      const raw = localStorage.getItem(clave);
      estado = raw ? JSON.parse(raw) : null;
    } catch {
      estado = null;
    }
    if (!estado) return;

    const ahora = Date.now();
    this.cicloInicio = estado.cicloInicio ?? ahora;

    // Descarta un descanso persistido inválido (mayor que la duración
    // configurada + margen): dato viejo/corrupto no debe reabrirse siempre.
    const restanteSeg = estado.descansoHasta
      ? Math.round((estado.descansoHasta - ahora) / 1000)
      : 0;
    const maxSeg = config.limTiempoDescanso * 60 + 5;
    if (estado.descansoHasta && restanteSeg > maxSeg) {
      this.reiniciarCiclo();
      this.persistir();
      return;
    }

    if (estado.descansoHasta && estado.descansoHasta > ahora) {
      // Seguía en descanso: reabrir con lo que resta, igual de minimizado/
      // expandido, en la misma posición, y mantener pausada la inactividad.
      this.descansoHasta = estado.descansoHasta;
      this.minimizado = !!estado.minimizado;
      this.posMinimizado = estado.posMinimizado;
      this.posExpandido = estado.posExpandido;
      this.avisado = true;
      const seg = Math.round((estado.descansoHasta - ahora) / 1000);
      this.ngZone.run(() => this.abrirBanner(config, seg));
      this.enDescanso$.next(true);
      return;
    }

    if (estado.descansoHasta) {
      // El descanso terminó durante el refresco → ciclo cumplido.
      this.reiniciarCiclo();
      this.persistir();
      return;
    }

    // Sin descanso: restaurar el uso acumulado.
    this.msActivos = estado.msActivos ?? 0;
  }

  /** Desarma el control: sampler y banner incluidos. Llamar cuando el usuario
   *  sale del shell de la app (destroy de NavigationComponent). */
  public detener(): void {
    this.activo = false;
    clearInterval(this.sampler!);
    this.sampler = undefined;
    this.reiniciarCiclo();
    this.avisado = false;
    this.enDescanso$.next(false);
    // close() dispara afterClosed; como no completó el descanso, no resetea de
    // más (ya reiniciamos arriba).
    this.dialogRef?.close();
  }
}
